import { css } from '@emotion/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { usePageTitle, usePersistedInitialValue } from '../../Lib';

import { ApiReportType } from '@kaiizen/kai-pro-api';
import { useAuth, useFeatureFlags, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getAllTasksById, getScanGroup, getScanTask, isGroupScoped, isTaskScoped, type ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { getGroupTitle, getTaskTitle, UNASSIGNED_CONTRACTOR_ID } from '@/Client/Lib/scopeNavigatorPartPresentersLib';

import type { ScanFocusPanelPage } from '@/Client/Partials/FocusPanel';
import {
  FLOOR_PLAN_PAGE,
  FocusedArtifactPanel,
  FocusedEditPanel,
  FocusedMeasurementsPanel,
  FocusedProgressAltPanel,
  FocusedProjectOverviewPanel,
  getScanFocusPanelScopeLevel,
  PROGRESS_ALT_FULLSCREEN_PAGE,
  PROGRESS_ALT_PAGE,
} from '@/Client/Partials/FocusPanel';
import type { MainNavigationActionArgs } from '@/Client/Partials/MainNavigation';
import { WorkspaceLayout } from '@kaiizen/kai-shiki/react';
import type { ProjectLifecycleStateType } from '../../Layout';
import { isScopeLocked, PageLayout, useProjectStateLifecycle } from '../../Layout';
import {
  AIScanVideoUploadModal,
  AllScanPhotos,
  ProgressEditor,
  ProjectMeasurementsModal,
  ScanLargePhotoViewer,
  ScanReview,
} from '../../Partials';
import { useAIScanVideoActiveWork } from '../../Partials/AIScanVideoUploadModal/useAIScanVideoActiveWork';
import { GalleryPhotoModal, type GalleryScopeTarget } from '../../Partials/FocusedPhotoGallery';
import {
  DndProvider,
  getLegacyExpandedTaskIdFromScanSearchParams,
  getScanIdFromPathname,
  getScanUrl,
  getScanUrlParent,
  getValidatedScanUrlParts,
  mergeScanSearchParams,
  NotesDrawerProvider,
  PhotoUploadProgressProvider,
  ScopeAttentionFilterProvider,
  ShowNotesProvider,
  useAutoSwitchOrgForResource,
  useElementSize,
  useNavigateToScan,
  useNavigateToScanFocusPanelPage,
} from '../../State';
import { TYPOGRAPHY2 } from '../../Styles';
import { PhotoUploadProgressIndicator } from '../../Widgets';

import { KaiNavigator } from '@/Client/Partials/KaiNavigator';
import { ProjectStageStepper, SCOPE_LOCKED_PROJECT_STAGE_STEPPER_CONFIG, TurnResponsibilityChip } from '@/Client/Partials/ProjectStageStepper';
import { ScopeNotesDrawer } from '@/Client/Partials/ScopeNotesDrawer/ScopeNotesDrawer';
import { useNavigateToProject } from '../../State/kaiNavigation';
import { useGuardedNavigate } from '../../State/NavigationGuardContext';
import { GenericFailurePage, GenericLoadingPage } from '../GenericPages';
import { useAdminScanPageNavigation } from './useAdminScanPageNavigation';
import { useAdminScanPageScopeSidebarActions } from './useAdminScanPageScopeSidebarActions';
import { useAdminScanPageScopeSidebarData } from './useAdminScanPageScopeSidebarData';
import { useKaiNavigatorPhotoDrop } from './useKaiNavigatorPhotoDrop';

const PANEL_CONTENT_STYLE = css`
  label: PANEL_CONTENT_STYLE;
  flex: 1;
  ${TYPOGRAPHY2.xSmall}
  background: var(--background2);
  width: 100%;
  display: flex;
  height: 100%;
  min-height: 0;
`;

const MAIN_CONTENT_STYLE = css`
  label: MAIN_CONTENT_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;

  overflow: hidden;
  min-height: 0;
  min-width: 340px
`;

const MIDDLE_CONTENT_STYLE = css`
  label: MIDDLE_CONTENT_STYLE;
  /* The notes drawer anchors to this box: it covers the right of the workspace, never the navigator. */
  position: relative;
  flex: 1;
  display: flex;
  min-width: 0;
  height: 100%;
`;

/** kai-shiki's WorkspaceLayout clamps the sidebar while dragging but trusts
 *  `sidebarFraction` on mount, so a stale or hand-edited localStorage value would
 *  render at whatever it says. Anything outside the open interval falls back to
 *  undefined, which lets WorkspaceLayout apply its own default rather than
 *  duplicating that number here. */
const sanitizedNavigatorSidebarFraction = (fraction: number | undefined) => typeof fraction === 'number' && fraction > 0 && fraction < 1 ? fraction : undefined;

export const AdminScanPageNew = ({ review = false, focusPanel, reportType, scanGroupId, scanContractorId, scanTaskId, artifactScanId, progress = false }: {
  review?: boolean;
  focusPanel?: ScanFocusPanelPage;
  reportType?: ApiReportType;
  scanGroupId?: string;
  /** Org contractor id from the /contractor route — the Contractor tab's selection. */
  scanContractorId?: string;
  scanTaskId?: string;
  artifactScanId?: string;
  progress?: boolean;
}) => {
  const showAllScanPhotos = focusPanel === 'photos';
  // scan-editing context
  const {
    scanId,
    scan,
    scanMutators,
    scanDataMutators,
    scanRoot,
    errorInfo,
    project,
    property,
    isError,
    error,
    getContractorForTask,
    contractorsMap,
    effectiveScanType,
    scanIsLatestApproved,
    workStatus,
    closeoutIsApproved,
    approvedScopeCount,
    projectIsEditable,
    defaultContractorsByTrade,
    baselineDefaultContractorsByTrade,
    showScanChanges,
    latestScan,
    latestApprovedScan,
  } = useScanEditingContextSlice([
    'scanId',
    'scan',
    'scanMutators',
    'scanDataMutators',
    'scanRoot',
    'errorInfo',
    'project',
    'property',
    'isError',
    'error',
    'getContractorForTask',
    'contractorsMap',
    'effectiveScanType',
    'scanIsLatestApproved',
    'workStatus',
    'closeoutIsApproved',
    'approvedScopeCount',
    'projectIsEditable',
    'defaultContractorsByTrade',
    'baselineDefaultContractorsByTrade',
    'showScanChanges',
    'latestScan',
    'latestApprovedScan',
  ]);
  const { featureFlags } = useFeatureFlags();
  const { user } = useAuth();
  usePageTitle(property?.address ? `Scan · ${property.address}` : undefined);

  useAutoSwitchOrgForResource(
    { error: isError ? error : undefined, scanId },
  );

  // routing
  const navigate = useNavigate();
  const guardedNavigate = useGuardedNavigate();
  const location = useLocation();
  const navigateToProject = useNavigateToProject();
  const navigateToScan = useNavigateToScan();
  const navigateToScanFocusPanelPage = useNavigateToScanFocusPanelPage();
  const [searchParams] = useSearchParams();
  const photoId = searchParams.get('photoId') || undefined;
  const taskIdFromUrl = getLegacyExpandedTaskIdFromScanSearchParams(searchParams, scanTaskId);

  const rootElementRef = useRef<HTMLDivElement>(null);
  const { width } = useElementSize(rootElementRef as any);
  const [persistedNavigatorSidebarFraction, persistNavigatorSidebarFraction] = usePersistedInitialValue<
    number | undefined
  >('scanEditor:navigatorSidebarFraction', undefined);

  // TODO: `filter` is ALWAYS empty and never set in this file. Remove it?
  const [filter, setFilter] = useState('');
  // TODO: `contractorFilterId` is ALWAYS undefined in this file as it is never set in this file. Remove it?
  const [contractorFilterId, setContractorFilterId] = useState<string | undefined>(undefined);
  const [projectMeasurementsModalOpen, setProjectMeasurementsModalOpen] = useState(false);
  const [aiVideoUploadModalOpen, setAiVideoUploadModalOpen] = useState(false);
  const [aiVideoUploadModalVariant, setAiVideoUploadModalVariant] = useState<'origin' | 'add'>('origin');
  // TODO: This is ALWAYS undefined as `filter` is NEVER set in this file. Remove this?
  const filterRegex = useMemo(() => {
    if (!filter) return undefined;
    return new RegExp(filter.trim().replace(/\s+/g, '.*'), 'i');
  }, [filter]);

  const useRoomMeasurements = !!featureFlags.useRoomMeasurements;
  const aiScanVideoUploadContextEnabled = !!latestScan
    && projectIsEditable
    && featureFlags.useAiVideoScan;
  const walkthroughUploadEnabled = aiScanVideoUploadContextEnabled
    && !latestApprovedScan
    && latestScan.scanLifecycleStatus === 'dispatched';
  const openAiVideoUploadModal = useCallback((variant: 'origin' | 'add' = 'origin') => {
    setAiVideoUploadModalVariant(variant);
    setAiVideoUploadModalOpen(true);
  }, []);

  const aiScanVideoActiveWork = useAIScanVideoActiveWork(
    aiScanVideoUploadContextEnabled && latestScan ? latestScan.id : '',
    aiScanVideoUploadContextEnabled,
  );
  const { refetch: refetchAiScanVideoActiveWork } = aiScanVideoActiveWork;
  const closeAiVideoUploadModal = useCallback(() => {
    setAiVideoUploadModalOpen(false);
    void refetchAiScanVideoActiveWork();
  }, [refetchAiScanVideoActiveWork]);

  const openProjectMeasurementsModal = useCallback(
    () => {
      if (useRoomMeasurements) {
        setProjectMeasurementsModalOpen(true);
        return;
      }
      if (project?.id) {
        navigateToProject({ projectId: project.id });
      }
    },
    [navigateToProject, project, useRoomMeasurements],
  );
  const closeProjectMeasurementsModal = useCallback(
    () => setProjectMeasurementsModalOpen(false),
    [],
  );

  const [pendingRoomMeasurementsGroupId, setPendingRoomMeasurementsGroupId] = useState<string | null>(null);
  const consumePendingRoomMeasurementsOpen = useCallback(
    () => setPendingRoomMeasurementsGroupId(null),
    [],
  );
  const selectGroupFromProjectMeasurementsModal = useCallback(
    (groupId: string) => {
      closeProjectMeasurementsModal();
      navigateToScan({ scanId, groupId, progress });
      setPendingRoomMeasurementsGroupId(groupId);
    },
    [closeProjectMeasurementsModal, navigateToScan, progress, scanId],
  );
  // **************************************************************
  // Project State Lifecycle
  // **************************************************************
  const projectStateLifecycle = useProjectStateLifecycle();

  const projectLifecycleState: ProjectLifecycleStateType = {
    phase: projectStateLifecycle,
    version: effectiveScanType === 'changeRequest' ? 'changeOrder' : 'initial',
    workStarted: workStatus !== 'notStarted',
    workComplete: workStatus === 'completed',
    closeoutApproved: !!closeoutIsApproved,
    projectIsEditable: !!projectIsEditable,
    scanIsLatestApproved: !!scanIsLatestApproved,
    approvedScopeCount: approvedScopeCount ?? 0,
  };

  const mainNavigationArgs: MainNavigationActionArgs = {
    state: projectLifecycleState,
    scan,
    scanMutators,
    scanRoot,
  };
  const turnResponsibility = featureFlags.turnBasedHandoff && project?.id && project.responsibleUserId
    ? {
      artifactType: projectLifecycleState.version === 'changeOrder' ? 'change order' : 'scope',
      canResponsibleUserApprove: (project.managerIds ?? []).includes(project.responsibleUserId),
      projectId: project.id,
      responsibleUserId: project.responsibleUserId,
      responsibleUserAssignedAt: project.responsibleUserAssignedAt,
    }
    : undefined;

  // **************************************************************
  // Memoized Locally relevant data
  // **************************************************************

  const { scopedGroups, group, task, scopedTasks, scanUrlParts } = useMemo(() => {
    const scanUrlParts = getValidatedScanUrlParts({
      scanRoot,
      scanId,
      groupId: scanGroupId,
      contractorId: scanContractorId,
      taskId: scanTaskId,
      review,
      focusPanel,
      progress,
      scope: reportType === 'fullScope' ? true : false,
      punchList: reportType === 'punchlist' ? true : false,
      closeout: reportType === 'closeout' ? true : false,
      changeOrder: reportType === 'changeOrder' ? true : false,
    });
    const { groupId, taskId } = scanUrlParts;
    const scopedGroups = scanRoot?.groups?.filter(isGroupScoped) ?? [];
    const group = scanRoot && groupId ? getScanGroup(scanRoot, groupId) : undefined;
    /* On a contractor route there is no group to look inside, so the task is
       found across the scan. */
    const task = scanRoot && taskId
      ? group
        ? getScanTask(scanRoot, group.id, taskId)
        : getAllTasksById(scanRoot)[taskId]
      : undefined;
    const scopedTasks = group?.tasks?.filter(isTaskScoped) ?? [];
    return { scopedGroups, group, task, scopedTasks, scanUrlParts };
  }, [
    progress,
    reportType,
    review,
    scanGroupId,
    scanContractorId,
    scanRoot,
    scanId,
    scanTaskId,
    focusPanel,
  ]);

  const selectedTaskId = task?.id ?? taskIdFromUrl;

  const redirectDestination = useMemo(() => {
    // can't redirect if we're in print preview or if we don't have a scan or scan root
    if (reportType || !scan || !scanRoot) return null;

    // couldn't find the group or task in the url, so go up one level
    if ((scanGroupId && !group) || (scanTaskId && !task)) return getScanUrlParent(scanUrlParts);

    /* Landing on a legacy scan-workspace route always goes to the Edit panel,
       whatever the project's lifecycle state. A contractor route lands here with
       no focus panel too, same as a bare group route, so it gets the same
       redirect with the contractor carried along instead of a group. review is
       included too: it's the legacy experience's default landing route (still
       reachable via browser history, e.g. the back button, after opting in),
       and the new experience has no review-mode equivalent of its own; the
       review the legacy page draws is built into the new editor's own
       header/chrome instead, so this redirect is what keeps a reader from
       landing on the old ScanReview fallback under the new sidebar. */
    const onLegacyScanWorkspaceRoute = !progress && !focusPanel;
    if (onLegacyScanWorkspaceRoute) {
      return {
        scanId,
        groupId: group?.id,
        contractorId: scanUrlParts.contractorId,
        taskId: task?.id,
        focusPanel: 'edit' as const,
      };
    }

    // Walkthrough playback and narration editing live in the scope editor now.
    if (focusPanel === 'videos') {
      return {
        scanId,
        groupId: group?.id,
        contractorId: scanUrlParts.contractorId,
        taskId: task?.id,
        focusPanel: 'edit' as const,
      };
    }

    // the rest of the cases only apply if we're in final or progress scan type, so if we're not, don't redirect
    if (progress && !group) {
      const wantGroup: ScanGroupOptimized | undefined = scopedGroups[0];
      const wantTask = wantGroup?.tasks?.find(isTaskScoped);
      if (wantGroup !== group || wantTask !== task) {
        return { scanId, groupId: wantGroup?.id, taskId: wantTask?.id, progress: true };
      }
    }
  }, [
    group,
    progress,
    focusPanel,
    reportType,
    scan,
    scanGroupId,
    scanId,
    scanRoot,
    scanTaskId,
    scanUrlParts,
    scopedGroups,
    task,
  ]);

  useEffect(() => {
    if (!redirectDestination) return;
    const redirectUrl = getScanUrl(redirectDestination);
    if (location.pathname === redirectUrl) return;
    /**
     * Canonicalizing the route carries the query with it. A deep link arrives, gets tidied up to the
     * scope it can actually resolve, and the grid it named has to survive that tidying — otherwise the
     * link half-works: right scope, default grid.
     */
    const sameScan = getScanIdFromPathname(location.pathname) === redirectDestination.scanId;
    navigate(mergeScanSearchParams(redirectUrl, location.search, sameScan), { replace: true });
  }, [navigate, redirectDestination, location.pathname, location.search]);

  const {
    focusPanelNavigationActive,
    keepFocusPanelOnScopeNavigation,
    exitLargePhotoViewer,
    navigateWithinFocusPanel,
    selectAllFocusPanelScope,
    navigateToFocusPanelPage,
    setCurrentGroupIdAndTaskId,
    setCurrentContractorIdAndTaskId,
    onBack,
  } = useAdminScanPageNavigation({
    scanId,
    scanUrlParts,
    focusPanel,
    progress,
    groupId: group?.id,
    taskId: task?.id,
    featureFlags,
    location,
    navigate: guardedNavigate,
    navigateToScan,
    navigateToScanFocusPanelPage,
    navigateToProject,
    projectId: project?.id,
  });

  const selectContractor = useCallback(
    (contractorId: string) => setCurrentContractorIdAndTaskId(contractorId, undefined),
    [setCurrentContractorIdAndTaskId],
  );

  /** Direct-by-id, not via a task's own `getContractorForTask` resolution, so it
   *  still resolves on a bucket-only route with no task selected, and always
   *  names the bucket the route is actually on rather than a task's own
   *  (potentially different) assignment. */
  const contractorTitle = scanUrlParts.contractorId
    ? scanUrlParts.contractorId === UNASSIGNED_CONTRACTOR_ID
      ? 'Unassigned'
      : contractorsMap?.[scanUrlParts.contractorId]?.name?.trim() || 'Unnamed contractor'
    : undefined;

  const focusPanelScopeProps = useMemo(() => ({
    scopeLevel: getScanFocusPanelScopeLevel({ groupId: group?.id, taskId: task?.id }),
    showProjectOverview: !!featureFlags.turnBasedHandoff,
    groupTitle: group ? getGroupTitle(group) : undefined,
    taskTitle: task ? getTaskTitle(task) : undefined,
    selectedGroupId: group?.id,
    selectedTaskId,
    contractorTitle,
    selectedContractorId: scanUrlParts.contractorId,
    onSelectContractor: selectContractor,
  }), [group, selectedTaskId, task, contractorTitle, featureFlags.turnBasedHandoff, scanUrlParts.contractorId, selectContractor]);

  const {
    sortedScanGroups,
    scopeAttentionEvalContext,
  } = useAdminScanPageScopeSidebarData();

  // sortedScanGroups (not scopedGroups) is what KaiNavigator's sidebar
  // actually renders - a group with no scoped tasks has zero cost and is
  // excluded from scopedGroups, so looking it up there would silently miss it
  // (empty title, no tabs) even though its photo button is right there in the
  // sidebar.
  const {
    selectCloseoutSidebarGroup,
    selectCloseoutSidebarTask,
    addTask,
    addCustomTask,
    canAddCustomTaskForGroup,
    cloneGroup,
    deleteGroup,
    deleteTask,
    duplicateTask,
  } = useAdminScanPageScopeSidebarActions({
    sortedScanGroups,
    group,
    focusPanelNavigationActive: keepFocusPanelOnScopeNavigation,
    navigateWithinFocusPanel,
    setCurrentGroupIdAndTaskId,
  });

  const handlePhotoDragEnd = useKaiNavigatorPhotoDrop();

  const syncGalleryActiveScope = useCallback(
    (target: GalleryScopeTarget) => {
      if (!showAllScanPhotos) return;
      navigateWithinFocusPanel({ ...target, replace: true });
    },
    [navigateWithinFocusPanel, showAllScanPhotos],
  );

  /**
   * Clearing the selection back to the whole scope. The Kai Navigator's Scope
   * header is the only way in, and it is always available — so this has to work
   * on every route that can represent "no group", not just the focus-panel ones
   * (`selectAllFocusPanelScope` no-ops without a focus panel).
   */
  const selectAllScope = useCallback(
    () =>
      keepFocusPanelOnScopeNavigation
        ? selectAllFocusPanelScope()
        : setCurrentGroupIdAndTaskId(undefined, undefined),
    [keepFocusPanelOnScopeNavigation, selectAllFocusPanelScope, setCurrentGroupIdAndTaskId],
  );

  // **************************************************************
  // First Returns
  // **************************************************************
  if (redirectDestination) return <GenericLoadingPage loadingMessage='Redirecting...' />;
  if (errorInfo) return <GenericFailurePage errorInfo={errorInfo} />;
  if (!scan || !scanRoot || !project || !property) {
    return (
      <GenericLoadingPage
        loadingMessage={!scan
          ? 'Loading scan...'
          : !scanRoot
          ? 'Loading scan data...'
          : !project
          ? 'Loading property project...'
          : !property
          ? 'Loading organization property...'
          : 'Loading...'}
      />
    );
  }

  // **************************************************************
  // Scan Navigator Left Panel
  // **************************************************************
  const showLeftPanel = width === 0 || width >= 1024; // width === 0 happens sometimes when we can't get the size of the window

  // **************************************************************
  // Main Content (Scan Editor and Progress Editor)
  // **************************************************************
  const showFullScreenPhotoViewer = !!photoId;
  const showGalleryPhotoModal = showAllScanPhotos && showFullScreenPhotoViewer;
  const hideWorkspaceForPhotoViewer = showFullScreenPhotoViewer && !showAllScanPhotos;

  const activeFocusPanelPage = focusPanel;

  const focusPanelScopeNavigation = focusPanelNavigationActive && activeFocusPanelPage
    ? {
      ...focusPanelScopeProps,
      activePanel: activeFocusPanelPage,
      onSelectPanel: navigateToFocusPanelPage,
      onSelectAll: selectAllFocusPanelScope,
      onSelectGroup: selectCloseoutSidebarGroup,
      onSelectTask: selectCloseoutSidebarTask,
    }
    : undefined;

  let mainContent: React.ReactNode | undefined = undefined;
  if (progress) {
    mainContent = (
      <ProgressEditor
        group={group}
        scopedGroups={scopedGroups}
        task={task}
        scopedTasks={scopedTasks}
        setCurrentGroupIdAndTaskId={setCurrentGroupIdAndTaskId}
      />
    );
  } else if (review) mainContent = <ScanReview filterRegex={filterRegex} />;
  // One panel for both of Progress Alt's pages, in the one slot — so React keeps it mounted across the
  // switch and none of what the reader had set up (period, tile size, what was expanded) is lost on the
  // way into fullscreen or back out. Which page it draws is the panel's own business; see
  // FocusedProgressAltPanel.
  else if (
    (focusPanel === PROGRESS_ALT_PAGE || focusPanel === PROGRESS_ALT_FULLSCREEN_PAGE)
    && focusPanelScopeNavigation
  ) {
    mainContent = <FocusedProgressAltPanel {...focusPanelScopeNavigation} />;
  } // One panel for both of Measurements' pages — the room index and the whole-property floor plan —
  // in the one slot, so the reader's work in the assembler survives a trip into a room and back.
  // Which page it draws is the panel's own business; see FocusedMeasurementsPanel.
  else if ((focusPanel === 'measurements' || focusPanel === FLOOR_PLAN_PAGE) && focusPanelScopeNavigation) {
    mainContent = (
      <FocusedMeasurementsPanel
        {...focusPanelScopeNavigation}
        groups={sortedScanGroups}
        listedSqft={property?.customerMetadata?.squareFeet ?? null}
        onOpenProjectMeasurements={openProjectMeasurementsModal}
      />
    );
  } else if (focusPanel === 'overview' && focusPanelScopeNavigation) {
    mainContent = <FocusedProjectOverviewPanel {...focusPanelScopeNavigation} />;
  } else if (focusPanel === 'edit' && focusPanelScopeNavigation) {
    mainContent = (
      <FocusedEditPanel
        {...focusPanelScopeNavigation}
        onOpenVideoUpload={openAiVideoUploadModal}
        aiScanVideoActiveWork={{
          activeCount: aiScanVideoActiveWork.activeCount,
        }}
        focusedScopeEditor={{
          contractorId: scanUrlParts.contractorId,
          filterRegex,
          group,
          groups: sortedScanGroups,
          projectStateLifecycle,
        }}
      />
    );
  } else if (focusPanel === 'artifact' && focusPanelScopeNavigation) {
    mainContent = <FocusedArtifactPanel {...focusPanelScopeNavigation} artifactScanId={artifactScanId} />;
  } else if (focusPanel === 'photos' && focusPanelScopeNavigation) {
    mainContent = (
      <AllScanPhotos
        filterRegex={filterRegex}
        focusPanelScopeNavigation={focusPanelScopeNavigation}
        onSelectAll={selectAllFocusPanelScope}
        onSelectGroup={selectCloseoutSidebarGroup}
        onSelectTask={selectCloseoutSidebarTask}
        onActiveScopeChange={syncGalleryActiveScope}
        walkthroughUploadEnabled={walkthroughUploadEnabled}
        onUploadWalkthrough={() => openAiVideoUploadModal('origin')}
      />
    );
  }

  // **************************************************************
  // LAYOUT
  // **************************************************************
  return (
    <ShowNotesProvider>
      <NotesDrawerProvider>
        <PageLayout
          key={'scan'}
          title={property?.address || scan.name}
          onBack={onBack}
          header={
            <ProjectStageStepper
              state={projectLifecycleState}
              config={isScopeLocked(projectLifecycleState) ? SCOPE_LOCKED_PROJECT_STAGE_STEPPER_CONFIG : undefined}
              renderTurnAccessory={turnResponsibility
                ? measurement => (
                  <TurnResponsibilityChip
                    artifactType={turnResponsibility.artifactType}
                    canResponsibleUserApprove={turnResponsibility.canResponsibleUserApprove}
                    projectId={turnResponsibility.projectId}
                    responsibleUserId={turnResponsibility.responsibleUserId}
                    responsibleUserAssignedAt={turnResponsibility.responsibleUserAssignedAt}
                    phase={projectLifecycleState.phase}
                    measurement={measurement}
                  />
                )
                : undefined}
            />
          }
          mainNavigationArgs={mainNavigationArgs}
          forceProjectLifecycleChrome
        >
          {latestScan && (
            <AIScanVideoUploadModal
              visible={aiVideoUploadModalOpen}
              onClose={closeAiVideoUploadModal}
              scanId={latestScan.id}
              variant={aiVideoUploadModalVariant}
              serverActiveWork={{
                items: aiScanVideoActiveWork.items,
                hasFetchedOnce: aiScanVideoActiveWork.hasFetchedOnce,
              }}
            />
          )}
          {useRoomMeasurements && (
            <ProjectMeasurementsModal
              listedSqft={property.customerMetadata?.squareFeet ?? null}
              onClose={closeProjectMeasurementsModal}
              onSelectGroup={selectGroupFromProjectMeasurementsModal}
              scanRoot={scanRoot}
              visible={projectMeasurementsModalOpen}
            />
          )}
          <ScopeAttentionFilterProvider evalContext={scopeAttentionEvalContext}>
            {
              /* Its own DndContext, nested inside the app-level one from App.tsx: drags
              started here (photo thumbnails in the Focus Panel) never leak out to
              the legacy ScanEditor's own drag-and-drop, and this handler gets full
              access to scan-editing context the app-level one doesn't have. */
            }
            <DndProvider onDragEnd={handlePhotoDragEnd}>
              <PhotoUploadProgressProvider>
                <PhotoUploadProgressIndicator />
                <div css={PANEL_CONTENT_STYLE} ref={rootElementRef}>
                  {showLeftPanel
                    ? (
                      <WorkspaceLayout
                        sidebarFraction={sanitizedNavigatorSidebarFraction(persistedNavigatorSidebarFraction)}
                        onResize={persistNavigatorSidebarFraction}
                        sidebar={
                          <KaiNavigator
                            groups={sortedScanGroups}
                            selectedGroupId={group?.id}
                            selectedContractorId={scanUrlParts.contractorId}
                            selectedTaskId={selectedTaskId}
                            scopeLocked={isScopeLocked(projectLifecycleState)}
                            onCollapse={() => undefined}
                            onSelectAll={selectAllScope}
                            onSelectGroup={selectCloseoutSidebarGroup}
                            onSelectTask={selectCloseoutSidebarTask}
                            onSelectContractor={selectContractor}
                            onSelectContractorTask={setCurrentContractorIdAndTaskId}
                            onCloneGroup={cloneGroup}
                            onDeleteGroup={deleteGroup}
                            onDeleteTask={deleteTask}
                            onDuplicateTask={duplicateTask}
                            onAddTask={addTask}
                            onAddCustomTask={addCustomTask}
                            canAddCustomTaskForGroup={canAddCustomTaskForGroup}
                          />
                        }
                        style={{ width: '100%', height: '100%' }}
                      >
                        <div css={[MIDDLE_CONTENT_STYLE, hideWorkspaceForPhotoViewer && css`display: none;`]}>
                          <div css={MAIN_CONTENT_STYLE}>{mainContent}</div>
                          <ScopeNotesDrawer selectedGroupId={group?.id} selectedTaskId={selectedTaskId} />
                        </div>
                      </WorkspaceLayout>
                    )
                    : (
                      <div css={[MIDDLE_CONTENT_STYLE, hideWorkspaceForPhotoViewer && css`display: none;`]}>
                        <div css={MAIN_CONTENT_STYLE}>{mainContent}</div>
                        <ScopeNotesDrawer selectedGroupId={group?.id} selectedTaskId={selectedTaskId} />
                      </div>
                    )}
                  {showGalleryPhotoModal && (
                    <GalleryPhotoModal onClose={exitLargePhotoViewer}>
                      <ScanLargePhotoViewer closePhotos={exitLargePhotoViewer} />
                    </GalleryPhotoModal>
                  )}
                  {showFullScreenPhotoViewer && !showAllScanPhotos && <ScanLargePhotoViewer closePhotos={exitLargePhotoViewer} />}
                </div>
              </PhotoUploadProgressProvider>
            </DndProvider>
          </ScopeAttentionFilterProvider>
        </PageLayout>
      </NotesDrawerProvider>
    </ShowNotesProvider>
  );
};
